import { Immutable } from 'nuclear-js'
import { ISelectedContent } from './modules/multipass-plan/interfaces';

export type SubscriptionState = 'active' | 'canceled' | 'expired' | 'pending' | 'paused';
type CustomerState = SubscriptionState;
export type BillingFrequency = 'monthly' | 'annually';

interface Customer {
  // from api
  id: number;
  multipass: boolean;
  canBeMigratedFromExpired: boolean;
  businessName: string;
  displayName: string;
  state: SubscriptionState;
  subscription: Subscription | MultipassSubscription;
  scheduledSubscription: MultipassSubscription;
  reactivationSubscription: MultipassSubscription;
  nextPostingTime: string;
  postsIncluded: number;
  postCreditsCount: number;
  postDeliveryMethod: 'hubspot' | 'wordpress' | 'alternate_email';
  emailForPosts: string;
  displayPublishedPosts: boolean;
  posts: Post[];
  keywords: Keyword[];
  updatedAt: string;
  createdAt: string;
  accountId: number;
  account: Account;
  unusedFreePostCouponCount: number;
  queuedPostsCount: number;
  numberOfOldPosts: number;
  numberOfOldCredits: number;

  website: string;
  blog: string;
  contact: string;
  twitter: string;
  facebook: string;
  slider1: number;
  slider2: number;
  slider3: number;
  slider4: number;
  slider5: number;
  primaryIndustryId: number;
  secondaryIndustryIds: number[];
  customIndustries: string[];
  otherInfo: string;
  audience: string;
  voiceAndPersonality: string;
  contentExample: string;
  languageCode: LanguageCode;

  activeKeywordsCount: number;
  assignedKeywordsCount: number;
  activeRequestsCount: number
  hasPlan: boolean;
  hasBpsPreference: boolean;
  planAddOnCode: 'option-a' | 'option-b' | 'option-c';
  proposedKeywordsCount: number;
  preferredWritersCount: number;
  canRequestKeywords: boolean;
  hubspotTokenVersion: number;
  enabledFeatures: ('lease_labs_pilot')[];

  // UI
  postsLoaded: { [context: string]: boolean};
  lastViewedPurchasePage: number;
  postCounts: { [context: string]: number };
  focusedPostId: number;

  couponApplications: CouponApplication[];
  expiresAt: string;
  keywordsWithBpsNeedsExist: boolean
  priceList: PriceList
}

const Languages = {
  'en-US': 'American English',
  'en-GB': 'British English',
  'en-AU': 'Australian English',
  'en-CA': 'Canadian English',
  'en-NZ': 'New Zealand English',
  'en-ZA': 'South African English'
}
type LanguageCode = keyof typeof Languages;

interface CustomerMeta {
  monthlyRevenue: number;
  declineRate: number;
  averageStarRating: number;
  postsReceived: number;
  preferredWritersCount: number;
  onSchedule: boolean;
  pastDue: boolean;
  oldPricing: boolean;
}

interface Post {
  // from api
  id: number;
  title: string;
  content: string;
  wordCount: number;
  state: 'new' | 'viewed' | 'declined' | 'posted' | 'edited' | 'edits_declined' | 'awaiting_editing' | 'withdrawn' | 'pinned'; // draft & pending not possible in this part of app
  customerId: number;
  keywordId: number;
  writerId: number;
  writer: Writer;
  writerPreferred: null | 'customer' | 'account';
  writerBlocked: null | 'customer' | 'account';
  appRating: Rating;
  appPostReview: PostReview;
  wlPostReview: PostReview;
  keyword: Keyword;
  customerPublished: boolean;
  postedDate: string;
  declinedDate: string;
  withdrawnDate: string;
  createdAt: string;
  updatedAt: string;
  lastTransitionAt: string;
  sentToCustomerAt: Date;
  submissionPosition: number;
  wlVisible: boolean;
  bpsEvents: {
    createdAt: number
    task: {
      code: 'video'
    }
  }[]

  // UI
  expanded: boolean;
}

interface Keyword {
  id: number;
  customerId: number;
  writerId: number;
  phrase: string;
  // postType: KeywordPostType;
  state: 'active' | 'assigned' | 'paused' | 'fulfilled' | 'deleted' | 'proposed' | 'draft' | 'declined';
  description: string;
  contentTypeName: string;
  priority: number;
  focusKeywords: string;
  link: string;
  linkText: string;
  externalLinks: boolean;
  seoNotes: string;
  createdAt: string;
  updatedAt: string;
  proposedAt: string;
  acceptedAt: string;
  deadline: string;
  timeZone: string;
  documents: Document[];
  postedCount: number;
  postLimit: number;
  businessName: string;
  writerPreferred: boolean;
  writer: Writer;
  contentType: ContentType;
  fromBuddy: boolean;
  previousWriterId: number;
  rewrite: boolean;
  quantityFromCredits: number;

  // UI
  expanded: boolean;
  editing: boolean;
}

interface HubspotPost {
  id: number,
  title: string,
  postBody: string,
  postSummary: string
}

interface ContentType {
  id: number;
  name: string;
}

interface Document {
  id: number;
  keywordId: number;
  name: string;
  path: string;
  fileUrl: string;

  // UI
  pending: boolean;
}

interface User {
  id: number;
  userableId: number;
  email: string;
  firstName: string;
  lastName: string;
  adminImpersonating: boolean;
  role: 'customer' | 'writer' | 'admin';
  memberAdmin: boolean;
  showTosMessage: boolean;
  acceptedTos: boolean;
  shownTooltips: Array<string>;
  userable: Writer;
}

interface Subscription {
  id: number;
  customerId: number;
  state: SubscriptionState;
  plan: Plan;
  hasAddons: boolean;
  currentPeriodEndsAt: string;
  canceledAt: string;
  expiresAt: string;
  pastDueAt: string;
  quantity: number;
  planAddOns: PlanAddOn[];
  totalPrice: number;
  totalPriceWithDiscount: number;
  planPrice: number;
  planPriceWithDiscount: number;
  addonsPrice: number;
  addonsPriceWithDiscount: number;
  extraPostPrice: number;
  extraPostAddonsPrice: number;
  nextCreditsIssuedAt: string;
  nextCreditsExpireAt: string;
  nextExpiringCreditsCount: number;
  extraVideoPrice: number;
  priceList: PriceList
  __typename: string

  durationInMonths?: any;
  selectedProducts?: any;
  scheduledToCancelAt: Date
  scheduledToPauseAt: Date
  subscriptionSummary: any;
}
interface MultipassSubscription {
  id: number
  customerId: number
  state: SubscriptionState
  currentPeriodEndsAt: string
  canceledAt: string
  expiresAt: string
  pastDueAt: string
  quantity: number
  totalPrice: number
  totalPriceWithDiscount: number
  nextCreditsIssuedAt: string
  nextCreditsExpireAt: string
  nextExpiringCreditsCount: number
  currentPeriodStartedAt: string
  durationInMonths: number
  priceList: PriceList
  selectedProducts: ISelectedContent[]
  __typename: string

  plan: undefined
  planAddOns: undefined
  hasAddons: undefined
  addonsPrice: undefined
  addonsPriceWithDiscount: undefined
  extraPostPrice: undefined
  extraPostAddonsPrice: undefined
  planPrice: undefined
  planPriceWithDiscount: undefined
  extraVideoPrice: undefined
  scheduledToCancelAt: Date
  scheduledToPauseAt: Date
  subscriptionSummary: {
    words: string | number;
    credits: number;
  };
}

interface AddOn {
  id: number
  addOnId: number
  code: string
  words: number
  valueInCredits: number
  formattedValue?: string
}

interface Product {
  id: number
  productId: number
  code: string
  words: number
  valueInCredits: number
  formattedValue?: string
  addOns?: { [key: string]: AddOn }
}

interface Service {
  id: number
  serviceId: number
  code: string
  valueInCents: number
  forSubsWithCreditsUpTo?: number
  formattedValue?: string
}

interface SubscriptionOptions {
  products: { [key: string]: Product }
  addOns: { [key: string]: Product }
  services: { [key: string]: Service }
}

interface PriceList extends SubscriptionOptions {
  creditPriceInCents: number
}

interface Plan {
  id: number;
  name: string;
  conciseName: string;
  code: string;
  words: number | 300 | 600 | 1000 | 1500 | 2000;
  price: number;
  numberOfPosts: number;
  billingFrequency: 'monthly' | 'annually';
  planAddOns: PlanAddOn[];
  active: boolean;
}

interface PlanAddOn {
  id: number;
  name: string;
  code: 'option-a' | 'option-b' | 'option-c' | 'option-h';
  price: number;
  planCode: string;
  extraPostPrice: number;
  active: boolean;
}

interface Account {
  id: number;
  accountName: string;
  customersCount: number;
  currentCustomersCount: number;
  fullySetUp: boolean;
  agency: boolean;
  accountProfile: AccountProfile;
  enabledFeatures: ('scheduling' | 'deadlines' | 'direct_assignments')[];
  monthlyRevenue: number; // dollars
  hasWhiteLabelProfile: boolean;
  video: boolean;

  // UI
  postCounts: { [writerId: number]: number };

  hasPlan: boolean;
  totalPrice: number;
  totalPriceWithDiscount: number;
  totalPendingValue: number;
  totalPendingValueWithDiscount: number;

  qualifiesForMultiDiscount: boolean;
  willQualifyForMultiDiscount: boolean;
  multiDiscountPercent: number;
  createdByAdmin: string;
}

interface AccountMeta {
  monthlyRevenue: number;
  declineRate: number;
  averageStarRating: number;
  activeSubscriptions: number;
  preferredWritersCount: number;
  pastDue: boolean;
  isAgency: boolean;
}

interface AccountProfile {
  id: number;
  account_id: number;
  clientsServed: string;
  marketingType: string;
  contactName: string;
  contactEmail: string;
  contactTitle: string;
  contactPhone: string;
  reference: string;
  referenceDetails: string;
  estimatedMonthlyBudget: string;
}

interface CouponApplication {
  id: number;
  expiresAt?: string;
  coupon: Coupon;
  usable: boolean;
  future: boolean;
  expired: boolean;
}

interface FullName {
  firstName: string;
  lastName: string;
}

interface Address {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface BillingInfo extends FullName, Address {
  company: string;
  maskedCc: string;
  // number, cvv, month, year are not managed by us
}

interface AbstractBillingAccount extends FullName {
  id: number;
  email: string;
  companyName: string;
  address: Address;
  method: 'credit_card' | 'paypal';
}

interface BillingAccount extends AbstractBillingAccount {
  billingInfo: undefined;
  token?: string;
}

interface BillingAccountWithBillingInfo extends AbstractBillingAccount {
  billingInfo: BillingInfo;
}

interface Coupon {
  id: number;
  externalName: string;
  couponCode: string;
  duration: 'forever' | 'signup' | 'second_month';
  discountType: 'percent' | 'cents' | 'free_post';
  discountInCents?: number;
  discountInDollars?: number;
  discountPercent?: number;
  quantity?: number;
  dollarLimit?: number;
}

interface Writer {
  id: number;
  userId: number;
  displayName: string;
  shortDisplayName: string;
  bio: string;
  slug: string;
  suspended: boolean;
  anonymous: boolean;
  points: number;
  level: number;
  postsByIndustry: Array<{count: number, id: number, name: string}>;
  publishedReviews: Array<string>;
  postsPublished: number;
  state: string;
  avatar: string;

  //navbar data
  proposedKeywordsNotificationCount: number;
  noKeywordsAlert: boolean;
  canReadDiscussion: boolean;
  canEditProfile: boolean;
  w9Record: boolean;
}

interface Rating {
  id: number;
  value: number;
  customerId: number;
  writerId: number;
  postId: number;
}

interface PostReview {
  id: number;
  content: string;
  customerId: number;
  writerId: number;
  postId: number;
}

interface WithId {
  id: number;
}

interface ToastPosition {
  x: 'left' | 'right';
  y: 'top' | 'bottom';
}

interface Country {
  id: string; // 'US'
  name: string; // 'United States'
}

interface State {
  id: string; // 'AL'
  name: string; // 'Alabama'
}

interface CustomerWriterAccess {
  id: number;
  writerId: number;
  customerId: number;
  accountId: number;
  access: 'preferred' | 'blocked';
  notified: boolean;
}

interface TimeZone {
  id: string; // America/Denver
  name: string; // (GMT-07:00) Mountain Time (US & Canada)
}
interface Alert {
  area: string;
  level: string;
  class: string;
  tooltip?: string;
  render: string;
}

interface Industry {
  id: number;
  name: string;
}

interface PostEditRequest {
  id: number;
  uniqId: string;
  postId: number;
  roundNumber: number;
  requestType: 'general' | 'task';
  state: 'created' | 'completed' | 'declined';
  content: string;
  postSelectedContent: string;
  createdAt: number;

  // private
  editing: boolean;
  _content: string;
}
export type WhiteLabelProfile = {
  id: number,
  name: string,
  textColor: string,
  backgroundColor: string
};


type PostExtraCode = 'video';
type CustomerExtraCode = 'ten_topics' | 'account_setup' | 'content_strategy';
type UserExtraCode = 'guided_tour';
type ExtraCode = PostExtraCode | CustomerExtraCode | UserExtraCode;

type StandardMap = Immutable.Map<string, any>;
type MapOfMaps = Immutable.Map<string, StandardMap>;
type MapOfMapsById = Immutable.Map<number, StandardMap>;

type StoreState = StandardMap;

export interface Article {
  title: string
  html: string
}

export interface EmptyFocusLength {
  empty: boolean
  focus: boolean
  length: number
}

export interface EmptyFocusLengthValid extends EmptyFocusLength {
  valid: boolean
}

export interface EmptyFocusLengthValidNumber extends EmptyFocusLengthValid {
  brand: string
  firstSix: string
  lastFour: string
}

export interface RecurlyState {
  fields: {
    month: EmptyFocusLength,
    year: EmptyFocusLengthValid,
    cvv: EmptyFocusLengthValid,
    number: EmptyFocusLengthValidNumber,
  }
}

export {
  Customer,
  CustomerMeta,
  Post,
  User,
  Subscription,
  MultipassSubscription,
  Plan,
  Writer,
  Keyword,
  HubspotPost,
  Rating,
  Document,
  StoreState,
  StandardMap,
  MapOfMaps,
  MapOfMapsById,
  WithId,
  ToastPosition,
  Account,
  AccountMeta,
  BillingAccount,
  BillingAccountWithBillingInfo,
  BillingInfo,
  PlanAddOn,
  Coupon,
  CouponApplication,
  Country,
  State,
  CustomerWriterAccess,
  TimeZone,
  Alert,
  PostReview,
  Industry,
  PostEditRequest,
  CustomerState,
  Languages,
  LanguageCode,
  PostExtraCode,
  CustomerExtraCode,
  UserExtraCode,
  ExtraCode,
  SubscriptionOptions,
  PriceList,
  Product,
  AddOn,
  Service,
}


export type Route = (string | number)[];
